import { AlertCircle, Check, Loader2 } from "lucide-react";
import { CopyButton } from "~/components/assistant/CopyButton";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import { ResourceLoadError } from "~/pages/manifold-nextgen/components/ResourceLoadError";
import type { SessionEvalRun } from "~/pages/manifold-nextgen/components/conversations/useSessionEvalRuns";
import { useEvalRunStatus } from "~/pages/manifold-nextgen/hooks/useEvalRunStatus";
import { evalRunLabel, EvalRunStatus } from "~/pages/manifold-nextgen/types/eval-run";
import { SessionContextDetailHeader } from "./SessionContextDetailHeader";
import type { SessionContextViewOption } from "./SessionContextDetailHeader";

// The launch UI is status-only (no report): liveness is shown by an
// indeterminate affordance and a lifecycle stepper, never a fabricated percent.
type StepState = "done" | "active" | "pending" | "failed";

interface EvalRunStatusDisplay {
  label: string;
  isLive: boolean;
  isCompleted: boolean;
  isFailed: boolean;
  runStep: StepState;
  terminalStep: StepState;
  terminalLabel: string;
}

// Maps the raw run status to the chip label + stepper state. `undefined` (first
// load, before the poll returns) reads as still-running, not terminal.
export function evalRunStatusDisplay(status: string | undefined): EvalRunStatusDisplay {
  switch (status) {
    case EvalRunStatus.PENDING:
      return {
        label: "Pending",
        isLive: true,
        isCompleted: false,
        isFailed: false,
        runStep: "pending",
        terminalStep: "pending",
        terminalLabel: "Succeeded",
      };
    case EvalRunStatus.SUCCEEDED:
      return {
        label: "Succeeded",
        isLive: false,
        isCompleted: true,
        isFailed: false,
        runStep: "done",
        terminalStep: "done",
        terminalLabel: "Succeeded",
      };
    case EvalRunStatus.FAILED:
      return {
        label: "Failed",
        isLive: false,
        isCompleted: false,
        isFailed: true,
        runStep: "done",
        terminalStep: "failed",
        terminalLabel: "Failed",
      };
    case EvalRunStatus.RUNNING:
    default:
      return {
        label: "Running",
        isLive: true,
        isCompleted: false,
        isFailed: false,
        runStep: "active",
        terminalStep: "pending",
        terminalLabel: "Succeeded",
      };
  }
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) {
    return "—";
  }
  // The backend can emit a doubly-terminated UTC offset ("+00:00Z"); drop the
  // stray trailing Z so Date.parse accepts it instead of falling through raw.
  const normalized = value.replace(/([+-]\d{2}:?\d{2})Z$/, "$1");
  const ms = Date.parse(normalized);
  if (Number.isNaN(ms)) {
    return value;
  }
  const date = new Date(ms);
  const datePart = date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const timePart = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `on ${datePart} at ${timePart}`;
}

interface SessionContextEvalRunDetailProps {
  runId: string;
  // The session's eval-run list, for the header switcher. Selecting one focuses
  // its row.
  evalRuns: SessionEvalRun[];
  onSelect: (runId: string) => void;
  onBack: () => void;
  onCollapse: () => void;
}

export const SessionContextEvalRunDetail = ({
  runId,
  evalRuns,
  onSelect,
  onBack,
  onCollapse,
}: SessionContextEvalRunDetailProps) => {
  const { data: run, error, isLoading } = useEvalRunStatus(runId);
  const display = evalRunStatusDisplay(run?.status);

  const views: SessionContextViewOption[] = evalRuns.map((r) => ({
    value: r.runId,
    label: evalRunLabel(r.tags, r.runId),
  }));

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <SessionContextDetailHeader
        entityName="Eval runs"
        onEntityClick={onBack}
        onBack={onBack}
        onCollapse={onCollapse}
        views={views}
        currentView={runId}
        onSetView={onSelect}
      />

      {display.isLive && (
        <div className="h-0.5 w-full overflow-hidden" role="progressbar" aria-label="Running">
          <div className="h-full w-full origin-left bg-primary animate-indeterminate-bar" />
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4">
        {isLoading && !run ? (
          <div className="flex flex-col gap-3" role="status" aria-label="Loading eval run">
            <Skeleton className="h-6 w-1/2 rounded-md" />
            <Skeleton className="h-24 rounded-lg" />
          </div>
        ) : error || !run ? (
          <ResourceLoadError error={error} resourceLabel="eval run" className="py-16" />
        ) : (
          <div className="flex flex-col gap-5">
            <dl className="flex flex-col gap-5">
              <Field label="Case tags">
                <span className="break-words text-xs text-foreground">
                  {run.tags.length > 0 ? run.tags.join(", ") : "All cases"}
                </span>
              </Field>
              {run.model_name && (
                <Field label="Model">
                  <code className="break-all rounded bg-background/60 font-mono text-xs text-foreground">
                    {run.model_name}
                  </code>
                </Field>
              )}
              <Field label="Run ID">
                <span className="break-all text-xs text-foreground">{runId}</span>
                <CopyButton
                  text={runId}
                  variant="ghost"
                  size="sm"
                  className="ml-1.5 align-middle"
                />
              </Field>
            </dl>

            <div>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Progress
              </h3>
              <ol className="flex flex-col">
                <Step
                  state="done"
                  label="Launched"
                  sub={`The run was launched ${formatTimestamp(run.started_at)}.`}
                  last={false}
                />
                <Step
                  state={display.runStep}
                  label="Running"
                  sub="Executing eval cases against the agent."
                  last={false}
                />
                <Step
                  state={display.terminalStep}
                  label={display.terminalLabel}
                  sub={
                    display.isCompleted || display.isFailed
                      ? `Finished ${formatTimestamp(run.completed_at)}.`
                      : "Reports the outcome when the run finishes."
                  }
                  last
                />
              </ol>
            </div>

            {display.isFailed && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
                <div className="mb-1.5 flex items-center gap-1.5">
                  <AlertCircle className="size-4 text-destructive" aria-hidden />
                  <span className="text-xs font-semibold text-destructive">Run failed</span>
                </div>
                <p className="break-words text-[11px] text-destructive">
                  The eval run failed. Open the run in the evals report for details.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</dt>
    <dd className="min-w-0">{children}</dd>
  </div>
);

const StepDot = ({ state }: { state: StepState }) => {
  if (state === "done") {
    return (
      <span className="flex size-5 items-center justify-center rounded-full bg-green-600 text-white dark:bg-green-500">
        <Check className="size-3" aria-hidden />
      </span>
    );
  }
  if (state === "failed") {
    return (
      <span className="flex size-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground">
        <AlertCircle className="size-3" aria-hidden />
      </span>
    );
  }
  if (state === "active") {
    return (
      <span className="flex size-5 items-center justify-center rounded-full text-primary">
        <Loader2 className="size-4 animate-spin" aria-hidden />
      </span>
    );
  }
  return <span className="size-5 rounded-full border-2 border-border bg-background" />;
};

const Step = ({
  state,
  label,
  sub,
  last,
}: {
  state: StepState;
  label: string;
  sub: string;
  last: boolean;
}) => (
  <li className="flex gap-3">
    <div className="flex flex-none flex-col items-center">
      <StepDot state={state} />
      {!last && <div className="my-1 w-0.5 flex-1 rounded-full bg-border" />}
    </div>
    <div className={cn("flex-1", last ? "pb-1" : "pb-4")}>
      <div className="text-sm font-semibold text-foreground">{label}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>
    </div>
  </li>
);
